import { createContext, useCallback, useContext, useMemo, useState } from "react";

/**
 * App-wide chat UI state. Separate from the chat messages themselves
 * (those live in useChat, scoped per-project) because this is just
 * the open/closed toggle + width + any "seed message" handoffs from
 * observation clicks.
 */
const ChatContext = createContext(null);

const MIN_WIDTH = 320;
const MAX_WIDTH = 720;
const DEFAULT_WIDTH = 420;
const STORAGE_KEY = "reno-chat-width";

export function ChatProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [pendingSeed, setPendingSeed] = useState(null);
  const [width, setWidthLocal] = useState(() => {
    const stored = Number(localStorage.getItem(STORAGE_KEY));
    if (!Number.isFinite(stored) || stored < MIN_WIDTH || stored > MAX_WIDTH) {
      return DEFAULT_WIDTH;
    }
    return stored;
  });

  const setWidth = useCallback((w) => {
    const clamped = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, w));
    setWidthLocal(clamped);
    localStorage.setItem(STORAGE_KEY, String(clamped));
  }, []);

  const openChat = useCallback(() => setOpen(true), []);
  const closeChat = useCallback(() => setOpen(false), []);
  const toggleChat = useCallback(() => setOpen((o) => !o), []);

  /**
   * Queue a prompt to be inserted into the composer when the chat opens.
   * Used when the user taps "!" on an observation — we pre-fill the input
   * with context so they can ask a follow-up.
   */
  const seedAndOpen = useCallback((seed) => {
    setPendingSeed(seed);
    setOpen(true);
  }, []);

  const consumeSeed = useCallback(() => {
    const s = pendingSeed;
    setPendingSeed(null);
    return s;
  }, [pendingSeed]);

  const value = useMemo(
    () => ({
      open,
      width,
      setWidth,
      openChat,
      closeChat,
      toggleChat,
      seedAndOpen,
      consumeSeed,
      MIN_WIDTH,
      MAX_WIDTH,
    }),
    [open, width, setWidth, openChat, closeChat, toggleChat, seedAndOpen, consumeSeed]
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChatUI() {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error("useChatUI must be used within ChatProvider");
  return ctx;
}
